import * as React from 'react';
import { useEffect, useState } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import user from 'js/lib/user';

import { CenterBox } from '@coursera/coursera-ui';
import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import { NotFound } from 'bundles/common/components/withNotFound';
import CourseUnauthorized from 'bundles/course-v2/components/CourseUnauthorized';
import { useCourseStoresLoading } from 'bundles/course-v2/hooks/useCourseStoresLoading';
import { useLoadComputedModels } from 'bundles/course-v2/hooks/useLoadComputedModels';
import { useLoadCourseBySlug } from 'bundles/course-v2/hooks/useLoadCourseBySlug';
import { useLoadCourseDeadlines } from 'bundles/course-v2/hooks/useLoadCourseDeadlines';
import { useLoadCourseMembership } from 'bundles/course-v2/hooks/useLoadCourseMembership';
import { useLoadCourseSessionGroup } from 'bundles/course-v2/hooks/useLoadCourseSessionGroup';
import { useLoadCourseStores } from 'bundles/course-v2/hooks/useLoadCourseStores';
import { useLoadVerificationDisplay } from 'bundles/course-v2/hooks/useLoadVerificationDisplay';
import { useShouldAllowLabPageAccess } from 'bundles/course-v2/hooks/useShouldAllowLabPageAccess';
import { useCustomLabelsByUserAndCourse } from 'bundles/custom-labels/hoc/withCustomLabelsByUserAndCourse';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';

const CourseDataProvider = ({ children }: React.PropsWithChildren) => {
  useLoadComputedModels();
  useLoadVerificationDisplay();
  useLoadCourseDeadlines();
  useLoadCourseSessionGroup();
  useLoadCourseStores();

  const { loading } = useCourseStoresLoading();
  if (loading) {
    return null;
  }

  return children;
};

const CourseMembershipProvider = ({ children }: React.PropsWithChildren) => {
  const { loading: courseLoading, error, courseId } = useLoadCourseBySlug();
  const { loading: courseMembershipLoading, isEnrolled } = useLoadCourseMembership();

  const shouldAllowLabPageAccess = useShouldAllowLabPageAccess();

  const loading = courseLoading || courseMembershipLoading;
  const authorized = !!courseId && (user.isSuperuser() || isEnrolled || shouldAllowLabPageAccess);

  if (error) {
    return <NotFound />;
  }

  if (loading) {
    return null;
  }

  if (!authorized) {
    return <CourseUnauthorized />;
  }

  return <CourseDataProvider>{children}</CourseDataProvider>;
};

const CourseProvider = ({ children }: React.PropsWithChildren) => {
  const { loading } = useCourseStoresLoading();

  const { courseId } = useFluxibleStore(CourseStore, (store) => ({ courseId: store.getCourseId() }));
  useCustomLabelsByUserAndCourse({ courseId });

  return (
    <div data-testid="course-provider">
      <CourseMembershipProvider>{children}</CourseMembershipProvider>
      {loading && (
        <CenterBox height={512}>
          <SvgLoaderSignal />
        </CenterBox>
      )}
    </div>
  );
};

const DeferToClientSideRender = ({ children }: React.PropsWithChildren) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return <CourseProvider>{children}</CourseProvider>;
};

export default DeferToClientSideRender;
